import React, { useEffect, useState, ReactElement } from 'react'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import styled from 'styled-components'

type Props = {
    dashboardName
}

const ThumbnailCard = ({ dashboardName }: Props): ReactElement => {
    const [editing, setEditing] = useState(false)
    const [fileLight, setFileLight] = useState<File>(null)
    const [fileDark, setFileDark] = useState<File>(null)
    const [thumbnailMetadata, setThumbnailMetadata] = useState<ThumbnailMetadata>()
    const [imageLight, setImageLight] = useState<string | null>(null)
    const [imageDark, setImageDark] = useState<string | null>(null)

    useEffect(() => {
        const url = `services/plugin-asset-metadata/thumbnail/thumbnailMetadata?dashboardName=${dashboardName}`

        fetch(url)
            .then(result => {
                if (result.ok) {
                    return result.json()
                } else {
                    throw new Error()
                }
            })

            .then(item => setThumbnailMetadata(item))
            .catch(e => globalNotification.error(e.message ?? i18n('An error occurred while fetching metadata')))
    }, [dashboardName])

    useEffect(() => {
        const url = `services/plugin-asset-metadata/thumbnail/download?mode=light&dashboardName=${dashboardName}`

        fetch(url)
            .then(result => result.blob())
            .then(blob => URL.createObjectURL(blob))
            .then(url => setImageLight(url))
            .catch(e => console.error('An error occurred while fetching thumbnail:', e.message))
    }, [thumbnailMetadata, fileLight, dashboardName, editing])

    useEffect(() => {
        const url = `services/plugin-asset-metadata/thumbnail/download?mode=dark&dashboardName=${dashboardName}`

        fetch(url)
            .then(result => result.blob())
            .then(blob => URL.createObjectURL(blob))
            .then(url => setImageDark(url))
            .catch(e => console.error('An error occurred while fetching thumbnail:', e.message))
    }, [thumbnailMetadata, fileDark, dashboardName, editing])

    const handleFileChangeLight = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files && event.target.files.length > 0) {
            setFileLight(event.target.files[0])
        }
    }

    const handleFileChangeDark = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files && event.target.files.length > 0) {
            setFileDark(event.target.files[0])
        }
    }

    const onDelete = (mode: string): void => {
        // Show a confirmation dialog before deleting
        const userConfirmed = confirm('Are you sure you want to delete?')

        // If the user confirmed, call the delete service
        if (userConfirmed) {
            const url = `services/plugin-asset-metadata/thumbnail/delete?mode=${mode}&dashboardName=${dashboardName}`
            fetch(url)
                .then()
                .catch(e => console.error('An error occurred while deleting thumbnail:', e.message))
        }
    }

    const handleSubmitLight = (event: React.FormEvent<HTMLFormElement>): void => {
        const url = `/services/plugin-asset-metadata/thumbnail/upload?mode=light&&dashboardName=${dashboardName}`
        event.preventDefault()
        submitFile(url, fileLight)
    }

    const handleSubmitDark = (event: React.FormEvent<HTMLFormElement>): void => {
        const url = `/services/plugin-asset-metadata/thumbnail/upload?mode=dark&&dashboardName=${dashboardName}`
        event.preventDefault()
        submitFile(url, fileDark)
    }

    const submitFile = (url, file): void => {
        if (!file) {
            console.error('no file')
        }
        const formData = new FormData()
        formData.append('file', file)

        fetch(url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin'
        })
            .then(response => {
                if (!response.ok) {
                    console.error('Upload failed')
                } else {
                    console.log('Uploaded')
                }
            })
            .then(toggleEdit)
            .catch(e => globalNotification.error(e.message ?? i18n('An error occurred while uploading thumbnail')))
    }

    const toggleEdit = (): void => setEditing(prev => !prev)
    const handleReset = (): void => {
        setEditing(false)
    }

    return (
        <StyledWebSettingsCard
            name="thumbnail"
            id="thumbnail"
            open={editing}
            toggle={() => toggleEdit()}
            onCancel={() => handleReset()}
        >
            <h2>{dashboardName}</h2>
            {thumbnailMetadata && dashboardName !== undefined && (
                <div className="flex">
                    <div className={`flex-1 ${editing ? 'open' : ''}`}>
                        <div className="gray-dark-bg" style={{ display: 'inline-block', padding: '8px' }}>
                            <figure>
                                <img src={imageLight} alt={'Thumbnail light'} />
                                <figcaption>
                                    <strong>Light Mode</strong>
                                </figcaption>
                            </figure>
                            {editing && (
                                <form onSubmit={handleSubmitLight}>
                                    <input type="file" onChange={handleFileChangeLight} />
                                    {fileLight && <button type="submit">Upload</button>}
                                    <button onClick={() => onDelete('light')}>Delete</button>
                                </form>
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="flex">
                                <span className="help-block">
                                    <i className="fa fa-info-circle" />
                                </span>
                                <span className="help-block">
                                    <span>
                                        This image will be displayed on the navigation page when live global theme is
                                        set to
                                    </span>{' '}
                                    <strong>light mode</strong>
                                </span>
                            </div>
                        </div>
                    </div>
                    <br />
                    <div className={`flex-1 ${editing ? 'open' : ''}`}>
                        <div className="gray-dark-bg" style={{ display: 'inline-block', padding: '8px' }}>
                            <figure>
                                <img src={imageDark} alt={'Thumbnail dark'} />
                                <figcaption>
                                    <strong>Dark Mode</strong>
                                </figcaption>
                            </figure>
                            {editing && (
                                <form onSubmit={handleSubmitDark}>
                                    <input type="file" onChange={handleFileChangeDark} />
                                    {fileDark && <button type="submit">Upload</button>}
                                    <button onClick={() => onDelete('dark')}>Delete</button>
                                </form>
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="flex">
                                <span className="help-block">
                                    <i className="fa fa-info-circle" />
                                </span>
                                <span className="help-block">
                                    <span>
                                        This image will be displayed on the navigation page when live global theme is
                                        set to
                                    </span>{' '}
                                    <strong>dark mode</strong>
                                </span>
                            </div>
                        </div>
                    </div>
                    <br />
                </div>
            )}
        </StyledWebSettingsCard>
    )
}

export default ThumbnailCard

const StyledWebSettingsCard = styled(WebSettingsCard)`
    //overflow: visible;
    flex-direction: column;
    max-width: fit-content;
`
